import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FiActivity, FiClock, FiCheckCircle, FiZap, FiRefreshCw,
  FiDownload, FiUpload, FiCpu, FiServer, FiDatabase,
} from 'react-icons/fi';
import {
  tokens, PageShell, PageWidth, PageHeader,
  Button, Card, CardHeader, CardTitle, StatGrid, StatCard, Badge,
} from '../../components/admin/AdminUI';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
  Title, Tooltip, Legend, Filler
);

const c = tokens.color;
const TARGET_LABEL = 'biz.antsnest.co.kr';
const PROBE_PATH = '/';
const INTERVAL_MS = 3000;
const MAX_POINTS = 30;

/* ── 토글 ── */
const Seg = styled.div`
  display: inline-flex; gap: 2px; background: ${c.surfaceMuted};
  border: 1px solid ${c.border}; border-radius: ${tokens.radius.md}; padding: 2px;
`;
const SegBtn = styled.button`
  border: none; cursor: pointer; font-family: inherit; font-size: 12px; font-weight: 600;
  padding: 5px 11px; border-radius: ${tokens.radius.sm};
  background: ${(p) => (p.$active ? '#fff' : 'transparent')};
  color: ${(p) => (p.$active ? c.primary : c.muted)};
  box-shadow: ${(p) => (p.$active ? tokens.shadow.xs : 'none')};
`;
const Controls = styled.div` display: flex; align-items: center; gap: 8px; flex-wrap: wrap; `;
const ChartWrap = styled.div` height: 320px; padding: 8px 4px 0; `;
const Meta = styled.div`
  display: flex; flex-wrap: wrap; gap: 8px 24px; font-size: 13px; color: ${c.muted}; margin-top: 14px;
`;
const Dot = styled.span`
  display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px;
  background: ${(p) => p.color || c.subtle};
`;

function ChartByType({ type, data, options }) {
  if (type === 'bar') return <Bar data={data} options={options} />;
  if (type === 'doughnut') return <Doughnut data={data} options={options} />;
  return <Line data={data} options={options} />;
}
function fmt(ms) { return ms == null ? '—' : `${Math.round(ms)} ms`; }
function fmtRate(kbs) { return kbs == null ? '—' : kbs >= 1024 ? `${(kbs / 1024).toFixed(2)} MB/s` : `${kbs.toFixed(1)} KB/s`; }

const PERIODS = [
  { k: 'live', label: '실시간' }, { k: 'hour', label: '시간별' },
  { k: 'day', label: '일별' }, { k: 'week', label: '주간' },
];
const HIST_PERIODS = [{ k: 'hour', label: '시간별' }, { k: 'day', label: '일별' }, { k: 'week', label: '주간' }];

export function Statistic() {
  const [samples, setSamples] = useState([]);
  const [traffic, setTraffic] = useState([]);
  const [sys, setSys] = useState(null);
  const [running, setRunning] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const timerRef = useRef(null);
  const lastTraffic = useRef(null);

  // 차트 컨트롤
  const [latType, setLatType] = useState('line');          // line | bar
  const [trafPeriod, setTrafPeriod] = useState('live');     // live | hour | day | week
  const [trafType, setTrafType] = useState('line');         // line | bar | doughnut
  const [usageMetric, setUsageMetric] = useState('cpu');    // cpu | rss | db
  const [usageRange, setUsageRange] = useState('hour');     // hour | day | week
  const [usageType, setUsageType] = useState('line');       // line | bar

  const [usageHist, setUsageHist] = useState([]);
  const [trafHist, setTrafHist] = useState([]);

  const probe = useCallback(async () => {
    const start = performance.now();
    let ok = false, ms = null;
    try {
      await fetch(`${PROBE_PATH}?_p=${start}`, { method: 'HEAD', cache: 'no-store', credentials: 'include' });
      ms = performance.now() - start; ok = true;
    } catch (e) { ok = false; ms = null; }
    try {
      const r = await fetch(`/app-traffic?_t=${start}`, { cache: 'no-store', credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        if (d.process || d.dbPool) setSys({ process: d.process, dbPool: d.dbPool });
        const prev = lastTraffic.current;
        if (prev) {
          const dt = Math.max(0.001, (d.now - prev.now) / 1000);
          const inKBs = Math.max(0, (d.inBytes - prev.inBytes) / 1024 / dt);
          const outKBs = Math.max(0, (d.outBytes - prev.outBytes) / 1024 / dt);
          setTraffic((a) => [...a, { t: new Date(), inKBs, outKBs }].slice(-MAX_POINTS));
        }
        lastTraffic.current = d;
      }
    } catch (e) { /* ignore */ }
    const now = new Date();
    setLastUpdate(now);
    setSamples((prev) => [...prev, { t: now, ms, ok }].slice(-MAX_POINTS));
  }, []);

  useEffect(() => {
    if (!running) return;
    probe();
    timerRef.current = setInterval(probe, INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [running, probe]);

  // 사용량 이력
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`/app-metrics-history?range=${usageRange}`, { cache: 'no-store', credentials: 'include' });
        if (r.ok) { const d = await r.json(); if (alive) setUsageHist(d.points || []); }
      } catch (e) {}
    };
    load(); const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [usageRange]);

  // 트래픽 이력 (실시간이 아닐 때)
  useEffect(() => {
    if (trafPeriod === 'live') return;
    let alive = true;
    const load = async () => {
      try {
        const r = await fetch(`/app-metrics-history?range=${trafPeriod}`, { cache: 'no-store', credentials: 'include' });
        if (r.ok) { const d = await r.json(); if (alive) setTrafHist(d.points || []); }
      } catch (e) {}
    };
    load(); const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [trafPeriod]);

  // ── KPI 계산 ──
  const okS = samples.filter((s) => s.ok && s.ms != null);
  const latest = samples[samples.length - 1];
  const current = latest && latest.ok ? latest.ms : null;
  const avg = okS.length ? okS.reduce((a, s) => a + s.ms, 0) / okS.length : null;
  const best = okS.length ? Math.min(...okS.map((s) => s.ms)) : null;
  const worst = okS.length ? Math.max(...okS.map((s) => s.ms)) : null;
  const successRate = samples.length ? Math.round((samples.filter((s) => s.ok).length / samples.length) * 100) : null;
  const online = latest ? latest.ok : null;
  const lt = traffic[traffic.length - 1];
  const grade = current == null ? { tone: 'neutral', text: '측정 중' }
    : current < 150 ? { tone: 'success', text: '빠름' }
    : current < 500 ? { tone: 'warning', text: '보통' } : { tone: 'danger', text: '느림' };

  const histLabel = (ts, range) => {
    const d = new Date(ts);
    return range === 'hour' ? `${String(d.getHours()).padStart(2, '0')}:00` : `${d.getMonth() + 1}/${d.getDate()}`;
  };

  // ── 응답시간 차트 ──
  const latencyData = {
    labels: samples.map((s) => s.t.toLocaleTimeString('ko-KR', { hour12: false, minute: '2-digit', second: '2-digit' })),
    datasets: [{
      label: '응답시간(ms)', data: samples.map((s) => (s.ok ? Math.round(s.ms) : null)),
      borderColor: c.primary, backgroundColor: latType === 'bar' ? c.primary : 'rgba(13,110,253,0.08)',
      borderWidth: 2, pointRadius: 2, tension: 0.35, fill: latType === 'line', spanGaps: true, borderRadius: 4,
    }],
  };

  // ── 트래픽 차트 ──
  const trafLive = trafPeriod === 'live';
  const trafPoints = trafLive ? traffic : trafHist;
  const trafUnit = trafLive ? 'KB/s' : 'MB';
  const inArr = trafLive ? trafPoints.map((p) => Number(p.inKBs.toFixed(2))) : trafPoints.map((p) => p.inMB);
  const outArr = trafLive ? trafPoints.map((p) => Number(p.outKBs.toFixed(2))) : trafPoints.map((p) => p.outMB);
  const trafLabels = trafLive
    ? trafPoints.map((p) => p.t.toLocaleTimeString('ko-KR', { hour12: false, minute: '2-digit', second: '2-digit' }))
    : trafPoints.map((p) => histLabel(p.ts, trafPeriod));
  const sumIn = inArr.reduce((a, b) => a + b, 0);
  const sumOut = outArr.reduce((a, b) => a + b, 0);
  const trafficData = trafType === 'doughnut'
    ? { labels: [`받는(${trafUnit})`, `보내는(${trafUnit})`], datasets: [{ data: [Number(sumIn.toFixed(2)), Number(sumOut.toFixed(2))], backgroundColor: [c.primary, c.teal], borderWidth: 0 }] }
    : {
        labels: trafLabels,
        datasets: [
          { label: `받는(${trafUnit})`, data: inArr, borderColor: c.primary, backgroundColor: trafType === 'bar' ? c.primary : 'rgba(13,110,253,0.08)', borderWidth: 2, pointRadius: 1, tension: 0.35, fill: trafType === 'line', borderRadius: 4 },
          { label: `보내는(${trafUnit})`, data: outArr, borderColor: c.teal, backgroundColor: trafType === 'bar' ? c.teal : 'transparent', borderWidth: 2, pointRadius: 1, tension: 0.35, fill: false, borderRadius: 4 },
        ],
      };

  // ── 사용량 이력 차트 ──
  const metricMeta = {
    cpu: { label: 'CPU %', pick: (p) => p.cpu, color: c.primary, unit: '%' },
    rss: { label: 'RAM(MB)', pick: (p) => p.rssMB, color: c.teal, unit: 'MB' },
    db: { label: 'DB 연결', pick: (p) => p.dbActive, color: '#9333ea', unit: '' },
  }[usageMetric];
  const usageData = {
    labels: usageHist.map((p) => histLabel(p.ts, usageRange)),
    datasets: [{
      label: metricMeta.label, data: usageHist.map(metricMeta.pick),
      borderColor: metricMeta.color, backgroundColor: usageType === 'bar' ? metricMeta.color : 'rgba(13,110,253,0.08)',
      borderWidth: 2, pointRadius: 1, tension: 0.35, fill: usageType === 'line', borderRadius: 4,
    }],
  };

  const lineOpt = (legend = false) => ({
    responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
    plugins: { legend: { display: legend, position: 'top', labels: { font: { size: 11 }, color: c.muted, boxWidth: 12 } } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, maxTicksLimit: 8, color: c.muted } },
      y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' }, ticks: { font: { size: 10 }, color: c.muted } },
    },
  });
  const donutOpt = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom', labels: { font: { size: 12 }, color: c.muted } } } };

  return (
    <PageShell>
      <PageWidth>
        <PageHeader
          title="대시보드"
          subtitle={`${TARGET_LABEL} 서비스 성능을 실시간·기간별로 모니터링합니다.`}
          actions={
            <Button $variant={running ? 'ghost' : 'primary'} onClick={() => setRunning((r) => !r)}>
              <FiRefreshCw /> {running ? '일시정지' : '재시작'}
            </Button>
          }
        />

        <StatGrid $cols={4}>
          <StatCard label="서비스 상태" value={online == null ? '측정 중' : online ? '정상' : '응답 없음'}
            color={online == null ? undefined : online ? c.successText : c.dangerText} icon={<FiActivity />}
            soft={online ? c.successSoft : c.dangerSoft} hint={TARGET_LABEL} />
          <StatCard label="현재 응답시간" value={fmt(current)} color={c.primary} icon={<FiZap />} soft={c.primarySoft} hint={`등급: ${grade.text}`} />
          <StatCard label="평균 응답시간" value={fmt(avg)} icon={<FiClock />} hint={best != null ? `최저 ${fmt(best)} · 최고 ${fmt(worst)}` : '—'} />
          <StatCard label="성공률" value={successRate == null ? '—' : `${successRate}%`} color={successRate === 100 ? c.successText : undefined} soft={c.successSoft} icon={<FiCheckCircle />} hint={`최근 ${samples.length}회`} />
        </StatGrid>

        <StatGrid $cols={4}>
          <StatCard label="프로세스 CPU" value={sys?.process ? `${sys.process.cpuPercent.toFixed(0)}%` : '—'} color={c.primary} soft={c.primarySoft} icon={<FiCpu />} hint={sys?.process ? `가동 ${Math.floor(sys.process.uptimeSec / 3600)}시간` : '측정 중'} />
          <StatCard label="프로세스 RAM" value={sys?.process ? `${Math.round(sys.process.rssMB)} MB` : '—'} icon={<FiServer />} hint={sys?.process ? `Heap ${Math.round(sys.process.heapUsedMB)} / ${Math.round(sys.process.heapTotalMB)} MB` : '측정 중'} />
          <StatCard label="DB 풀 사용" value={sys?.dbPool ? `${sys.dbPool.active} / ${sys.dbPool.max}` : '—'} soft={c.primarySoft} icon={<FiDatabase />} hint={sys?.dbPool ? `유휴 ${sys.dbPool.idle} · 전체 ${sys.dbPool.total}` : '측정 중'} />
          <StatCard label="현재 트래픽" value={fmtRate(lt ? lt.inKBs : null)} icon={<FiDownload />} hint={`보내기 ${fmtRate(lt ? lt.outKBs : null)}`} />
        </StatGrid>

        {/* 응답시간 */}
        <Card $pad={false}>
          <CardHeader>
            <CardTitle><FiActivity /> 응답시간 추이 <Badge $tone={grade.tone} $dot>{grade.text}</Badge></CardTitle>
            <Controls>
              <Seg>
                <SegBtn $active={latType === 'line'} onClick={() => setLatType('line')}>라인</SegBtn>
                <SegBtn $active={latType === 'bar'} onClick={() => setLatType('bar')}>바</SegBtn>
              </Seg>
            </Controls>
          </CardHeader>
          <div style={{ padding: '16px 20px 20px' }}>
            <ChartWrap><ChartByType type={latType} data={latencyData} options={lineOpt(false)} /></ChartWrap>
            <Meta>
              <span><Dot color={online ? c.successDot : c.dangerDot} />{online == null ? '대기' : online ? '온라인' : '오프라인'}</span>
              <span>측정 주기 {INTERVAL_MS / 1000}초 · 실시간</span>
              <span>마지막 {lastUpdate ? lastUpdate.toLocaleTimeString('ko-KR', { hour12: false }) : '—'}</span>
            </Meta>
          </div>
        </Card>

        {/* 트래픽 */}
        <Card $pad={false} style={{ marginTop: 16 }}>
          <CardHeader>
            <CardTitle><FiActivity /> 네트워크 트래픽</CardTitle>
            <Controls>
              <Seg>{PERIODS.map((p) => <SegBtn key={p.k} $active={trafPeriod === p.k} onClick={() => setTrafPeriod(p.k)}>{p.label}</SegBtn>)}</Seg>
              <Seg>
                <SegBtn $active={trafType === 'line'} onClick={() => setTrafType('line')}>라인</SegBtn>
                <SegBtn $active={trafType === 'bar'} onClick={() => setTrafType('bar')}>바</SegBtn>
                <SegBtn $active={trafType === 'doughnut'} onClick={() => setTrafType('doughnut')}>도넛</SegBtn>
              </Seg>
            </Controls>
          </CardHeader>
          <div style={{ padding: '16px 20px 20px' }}>
            <ChartWrap>
              {trafPoints.length === 0
                ? <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.subtle, fontSize: 13 }}>데이터 수집 중…</div>
                : <ChartByType type={trafType} data={trafficData} options={trafType === 'doughnut' ? donutOpt : lineOpt(true)} />}
            </ChartWrap>
            <Meta>
              <span><FiDownload size={13} style={{ verticalAlign: 'middle', marginRight: 4, color: c.primary }} />받는 = 들어오는 요청</span>
              <span><FiUpload size={13} style={{ verticalAlign: 'middle', marginRight: 4, color: c.teal }} />보내는 = 나가는 응답</span>
              <span>{trafLive ? '실시간(KB/s)' : `${PERIODS.find((p) => p.k === trafPeriod).label}(MB)`}</span>
            </Meta>
          </div>
        </Card>

        {/* 사용량 이력 */}
        <Card $pad={false} style={{ marginTop: 16 }}>
          <CardHeader>
            <CardTitle><FiClock /> 사용량 이력</CardTitle>
            <Controls>
              <Seg>
                <SegBtn $active={usageMetric === 'cpu'} onClick={() => setUsageMetric('cpu')}>CPU</SegBtn>
                <SegBtn $active={usageMetric === 'rss'} onClick={() => setUsageMetric('rss')}>RAM</SegBtn>
                <SegBtn $active={usageMetric === 'db'} onClick={() => setUsageMetric('db')}>DB</SegBtn>
              </Seg>
              <Seg>{HIST_PERIODS.map((p) => <SegBtn key={p.k} $active={usageRange === p.k} onClick={() => setUsageRange(p.k)}>{p.label}</SegBtn>)}</Seg>
              <Seg>
                <SegBtn $active={usageType === 'line'} onClick={() => setUsageType('line')}>라인</SegBtn>
                <SegBtn $active={usageType === 'bar'} onClick={() => setUsageType('bar')}>바</SegBtn>
              </Seg>
            </Controls>
          </CardHeader>
          <div style={{ padding: '16px 20px 20px' }}>
            <ChartWrap>
              {usageHist.length === 0
                ? <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.subtle, fontSize: 13 }}>수집된 이력이 없습니다. 5분 간격으로 쌓이며 최대 31일 보관됩니다.</div>
                : <ChartByType type={usageType} data={usageData} options={lineOpt(false)} />}
            </ChartWrap>
            <Meta>
              <span>지표: {metricMeta.label}</span>
              <span>구간: {usageRange === 'hour' ? '최근 24시간(시간별)' : usageRange === 'week' ? '최근 약 한달(주간)' : '최근 30일(일별)'}</span>
              <span>표본 {usageHist.length}개</span>
            </Meta>
          </div>
        </Card>
      </PageWidth>
    </PageShell>
  );
}

export default Statistic;
